import { Canvas, useFrame, useThree } from "@react-three/fiber";
import { Float, Text, OrbitControls, useCursor } from "@react-three/drei";
import { useRef, useState, useEffect, useMemo, useCallback, memo } from "react";
import * as THREE from "three";
import { useSpring, animated } from "@react-spring/three";
import { useTheme } from "@/theme/ThemeContext";

const WaveField = () => {
  const meshRef = useRef<THREE.Mesh>(null);
  const { viewport, mouse, camera } = useThree();
  const [hovered, setHovered] = useState(false);
  const { theme } = useTheme();
  useCursor(hovered);

  // Memoize geometry and material
  const geometry = useMemo(() => new THREE.PlaneGeometry(30, 30, 100, 100), []);
  const material = useMemo(() =>
    new THREE.MeshPhongMaterial({
      color: theme === "dark" ? 0xff6b35 : 0x000000,
      wireframe: true,
      transparent: true,
      opacity: 0.8,
      side: THREE.DoubleSide,
    }), [theme]
  );

  // Memoize vertex calculations
  const calculateVertexPosition = useCallback((vertex: THREE.Vector3, time: number, mouseX: number, mouseY: number) => {
    const distanceToMouse = Math.sqrt(
      Math.pow(vertex.x - mouseX, 2) + Math.pow(vertex.y - mouseY, 2)
    );
    const wave = Math.sin(time * 2 + distanceToMouse * 0.1) * 0.5;
    const mouseInfluence = Math.max(0, 1 - distanceToMouse / 10);
    const mouseWave = mouseInfluence * 2;
    return wave + mouseWave;
  }, []);

  useEffect(() => {
    if (meshRef.current) {
      (meshRef.current.material as THREE.MeshPhongMaterial).color.set(theme === "dark" ? 0xff6b35 : 0x000000);
    }
  }, [theme]);

  useFrame((state) => {
    if (!meshRef.current) return;

    const time = state.clock.getElapsedTime();
    const mouseX = (mouse.x * viewport.width) / 2;
    const mouseY = (mouse.y * viewport.height) / 2;

    // Smooth camera movement
    camera.position.x += (mouse.x * 3 - camera.position.x) * 0.05;
    camera.position.y += (-mouse.y * 3 - camera.position.y) * 0.05;
    camera.lookAt(0, 0, 0);

    const positions = meshRef.current.geometry.attributes.position;
    const vertex = new THREE.Vector3();

    // Batch update vertices
    for (let i = 0; i < positions.count; i++) {
      vertex.fromBufferAttribute(positions, i);
      positions.setZ(i, calculateVertexPosition(vertex, time, mouseX, mouseY));
    }
    positions.needsUpdate = true;
  });

  return (
    <mesh
      ref={meshRef}
      geometry={geometry}
      material={material}
      rotation={[-Math.PI / 2, 0, 0]}
      onPointerOver={() => setHovered(true)}
      onPointerOut={() => setHovered(false)}
    />
  );
};

const FloatingElement = memo(({ type, position, color }: { type: string; position: [number, number, number]; color: string }) => {
  const geometry = useMemo(() => {
    switch (type) {
      case "box":
        return <boxGeometry args={[1, 1, 1]} />;
      case "sphere":
        return <sphereGeometry args={[0.7, 32, 32]} />;
      case "torus":
        return <torusGeometry args={[0.7, 0.2, 16, 32]} />;
      default:
        return null;
    }
  }, [type]);

  return (
    <Float
      speed={2}
      rotationIntensity={1}
      floatIntensity={1}
      position={position}
    >
      <mesh>
        {geometry}
        <meshStandardMaterial
          color={color}
          metalness={0.8}
          roughness={0.2}
          transparent
          opacity={0.6}
          wireframe
        />
      </mesh>
    </Float>
  );
});

const FloatingElements = memo(() => {
  const { theme } = useTheme();
  const elements = useMemo(() => [
    {
      type: "box",
      position: [-3, 2, -2] as [number, number, number],
      color: theme === "dark" ? "#ff9f1c" : "#000000"
    },
    {
      type: "sphere",
      position: [3, -2, -1] as [number, number, number],
      color: theme === "dark" ? "#ffbf69" : "#000000"
    },
    {
      type: "torus",
      position: [0, 3, -3] as [number, number, number],
      color: theme === "dark" ? "#ff6b35" : "#000000"
    },
  ], [theme]);

  return (
    <>
      {elements.map((element, index) => (
        <FloatingElement key={index} {...element} />
      ))}
    </>
  );
});

const Scene = memo(() => {
  const { theme } = useTheme();
  const pointLightColor = useMemo(() => theme === "dark" ? "#ff6b35" : "#000000", [theme]);

  return (
    <>
      <ambientLight intensity={0.5} />
      <directionalLight position={[10, 10, 5]} intensity={1} />
      <pointLight
        position={[-10, -10, -5]}
        intensity={0.5}
        color={pointLightColor}
      />
      <WaveField />
      <FloatingElements />
      <OrbitControls enableZoom={false} enablePan={false} />
    </>
  );
});

const ThreeBackground = memo(() => {
  const { theme } = useTheme();
  return (
    <div className="absolute inset-0 -z-10">
      <Canvas
        camera={{ position: [0, 0, 10], fov: 75 }}
        gl={{ antialias: true, alpha: true }}
        dpr={[1, 2]} // Optimize for different device pixel ratios
      >
        <color attach="background" args={[theme === 'dark' ? '#000000' : 'transparent']} />
        <Scene />
      </Canvas>
    </div>
  );
});

export default ThreeBackground;
